import { useState, useMemo, useCallback } from "react"
import { columnSorters } from "../equipmentTable/columnSorters"

/**
 * Custom hook for managing equipment filtering, sorting, and pagination
 * @param {Array} equipmentData - Raw equipment data
 * @param {Array} categoryListing - Available categories
 * @param {Function} t - Translation function
 * @returns {Object} Filter states, filtered data, and handler functions
 */
export const useEquipmentFilters = (equipmentData, categoryListing, t) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [selectedCategories, setSelectedCategories] = useState([])
  const [sorter, setSorter] = useState({ columnKey: null, order: null })

  // Derive selected category names (handles 'all' sentinel & id type differences)
  const categoryNames = useMemo(() => {
    if (!Array.isArray(categoryListing) || !categoryListing.length) return []
    if (selectedCategories.includes("all")) return categoryListing.map((cat) => cat.name)
    if (!selectedCategories.length) return []
    const sel = new Set(selectedCategories.map(String))
    return categoryListing.filter((cat) => sel.has(String(cat.id))).map((cat) => cat.name)
  }, [categoryListing, selectedCategories])

  const categoryOptions = useMemo(() => {
    const options = categoryListing.map((cat) => ({
      label: cat?.name,
      value: cat?.id
    }))
    return [{ label: t("All") || "All", value: "all" }, ...options]
  }, [categoryListing, t])

  // Filtered data
  const filteredData = useMemo(() => {
    let data = equipmentData
    const searchLower = search.toLowerCase()

    if (searchLower) {
      data = data.filter((row) => row.title?.toLowerCase().includes(searchLower) || row.category?.toLowerCase().includes(searchLower))
    }

    if (selectedCategories.length && !selectedCategories.includes("all")) {
      const nameSet = new Set(categoryNames.map((n) => n.toLowerCase()))
      data = data.filter((row) => row.category && nameSet.has(row.category.toLowerCase()))
    }

    return data
  }, [equipmentData, search, selectedCategories, categoryNames])

  // Sorted data
  const sortedData = useMemo(() => {
    if (!sorter.columnKey || !sorter.order) return filteredData
    const col = columnSorters.find((c) => c.key === sorter.columnKey)
    if (!col?.sorter) return filteredData
    const sorted = [...filteredData].sort(col.sorter)
    if (sorter.order === "descend") {
      sorted.reverse()
    }
    return sorted
  }, [filteredData, sorter])

  // Paginated data
  const paginatedData = useMemo(() => {
    return sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  }, [sortedData, currentPage, pageSize])

  const handleSearchChange = useCallback((value) => {
    setSearch(value)
    setCurrentPage(1)
  }, [])

  const handleSortChange = useCallback((_, __, sorterObj) => {
    setSorter({ columnKey: sorterObj.columnKey, order: sorterObj.order })
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  return {
    // State
    search,
    selectedCategories,
    currentPage,
    pageSize,
    // Derived data
    categoryOptions,
    filteredData,
    paginatedData,
    // Handlers
    setSearch: handleSearchChange,
    setSelectedCategories,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange
  }
}
